import { ChangeEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { BaseInput } from "@kaltura/ds-react-components";
import { translate, translatePlural, postKmsData } from "@kaltura/mediaspace-shared-utils";
import { filterPlaylists, onInputKeyDown } from "../../helpers";
import { PlaylistsList } from "./PlaylistsList";
import { ChangesType, GetPlaylistsResult, PlaylistType } from "../AddToPlaylistModal";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { usePlaylistsListStore } from "../../playlistsListStore";
import { StyledSearch24Icon } from "../StyledComponents";
import { CreatePlaylist } from "../create-playlist/CreatePlaylist";
import { uniqueId } from "lodash";
import { useForm } from "react-hook-form";
import { SpanSrOnly } from "@kaltura/mediaspace-shared-ui";

const isEntryInitiallyInPlaylist = (playlist: PlaylistType, entryIds: string[]): boolean => {
    if (!playlist.playlistEntries || entryIds.length !== 1) {
        return false;
    }

    return playlist.playlistEntries.includes(entryIds[0]);
};

export const applyChangesToPlaylists = (
    data: GetPlaylistsResult | null,
    changes: ChangesType,
    entryIds: string[]
): { updatedOriginalPlaylists: PlaylistType[]; updatedAdditionalPlaylists: PlaylistType[] } => {
    const originalPlaylistsFromServer = data?.playlists ?? [];
    const additionalPlaylistsFromServer = data?.additionalPlaylists ?? [];

    const updatedOriginalPlaylists = originalPlaylistsFromServer.map((item) => {
        const baseSelected = isEntryInitiallyInPlaylist(item, entryIds);
        const override = changes[item.id];
        const isSelected = override !== undefined ? override : baseSelected;
        return {
            ...item,
            isSelected,
        };
    });

    const updatedAdditionalPlaylists = additionalPlaylistsFromServer.map((item) => {
        const baseSelected = isEntryInitiallyInPlaylist(item, entryIds);
        const override = changes[item.id];
        const isSelected = override !== undefined ? override : baseSelected;
        return {
            ...item,
            isSelected,
        };
    });

    return { updatedOriginalPlaylists, updatedAdditionalPlaylists };
};

interface PlaylistsListContainerProps {
    /** API endpoint used to load playlists */
    getPlaylistsUrl: string;
    /** Entries to add to playlists */
    entryIds: string[];
    /** Optional category/channel context */
    categoryId?: string;
    /** Whether creation of new playlist is allowed */
    allowCreate: boolean;
    /** KMS endpoint to post add-to-playlist changes */
    addToPlaylistUrl: string;
    /** Close handler passed from modal */
    onCloseMenu?: () => void;
    /** Notify parent about updated playlists */
    onItemStateChange?: (key: string, playlists: PlaylistType[]) => void;
    /** Form id used by external submit button */
    formId: string;
    /** Optional callback to expose minimal form state to parent */
    onFormStateChange?: (state: { isDirty: boolean; isSubmitting: boolean }) => void;
}

export const PlaylistsListContainer = (props: PlaylistsListContainerProps) => {
    const {
        getPlaylistsUrl,
        entryIds,
        categoryId,
        allowCreate,
        addToPlaylistUrl,
        onCloseMenu,
        onItemStateChange,
        formId,
        onFormStateChange,
    } = props;

    // Local state owned entirely by this container
    const [changes, setChanges] = useState<ChangesType>({});
    const [newPlaylists, setNewPlaylists] = useState<PlaylistType[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Generate unique IDs for accessibility
    const searchInputId = useId();
    const resultsListId = useId();

    const { showToast } = useToastsContext();
    const { handleSubmit, formState } = useForm();

    const { getPlaylists, invalidate } = usePlaylistsListStore();

    // Read the Suspense-backed data resource directly from the store
    const data = getPlaylists(getPlaylistsUrl, { entryIds });

    const { updatedOriginalPlaylists, updatedAdditionalPlaylists } = useMemo(
        () => applyChangesToPlaylists(data, changes, entryIds),
        [data, changes, entryIds]
    );

    const playlistsToShow = useMemo(
        () => filterPlaylists(updatedOriginalPlaylists, updatedAdditionalPlaylists, newPlaylists, searchTerm),
        [updatedOriginalPlaylists, updatedAdditionalPlaylists, newPlaylists, searchTerm]
    );

    const isDirty = Object.keys(changes).length > 0;

    // Expose minimal form state (dirty + submitting) to parent for the external button
    const lastReportedStateRef = useRef<{ isDirty: boolean; isSubmitting: boolean } | null>(null);
    useEffect(() => {
        if (!onFormStateChange) {
            return;
        }

        const next = { isDirty, isSubmitting: formState.isSubmitting };
        const last = lastReportedStateRef.current;

        if (!last || last.isDirty !== next.isDirty || last.isSubmitting !== next.isSubmitting) {
            lastReportedStateRef.current = next;
            onFormStateChange(next);
        }
    }, [isDirty, formState.isSubmitting, onFormStateChange]);

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleNewPlaylist = (name: string) => {
        const newPlaylist: PlaylistType = {
            name,
            id: uniqueId(),
            isSelected: true,
        };
        const changesCopy = { ...changes };
        changesCopy[newPlaylist.id] = true;
        setChanges(changesCopy);
        setNewPlaylists([...newPlaylists, newPlaylist]);
    };

    const handleCheckboxChange = (id: string, isChecked: boolean) => {
        const changesCopy = { ...changes };

        // first, handle new playlists which are only stored locally
        const newIndex = newPlaylists.findIndex((item) => item.id === id);
        if (newIndex >= 0) {
            const updatedNewPlaylists = [...newPlaylists];
            updatedNewPlaylists[newIndex] = {
                ...updatedNewPlaylists[newIndex],
                isSelected: isChecked,
            };
            setNewPlaylists(updatedNewPlaylists);

            if (id in changesCopy) {
                delete changesCopy[id];
            }
            else {
                changesCopy[id] = isChecked;
            }
        }
        else {
            const { playlists = [], additionalPlaylists = [] } = data;
            const allPlaylists = [...playlists, ...additionalPlaylists];
            const originalItem = allPlaylists.find((item) => item.id === id);

            if (originalItem) {
                const baseSelected = isEntryInitiallyInPlaylist(originalItem, entryIds);
                if (baseSelected === isChecked) {
                    delete changesCopy[id];
                }
                else {
                    changesCopy[id] = isChecked;
                }
            }
            else {
                // No baseline state from the server, so treat the default as "not selected".
                if (!isChecked && id in changesCopy) {
                    delete changesCopy[id];
                }
                else if (isChecked) {
                    changesCopy[id] = isChecked;
                }
            }
        }

        setChanges(changesCopy);
    };

    const onSubmit = async () => {
        const params = {
            entryIds,
            selectedPlaylist: updatedOriginalPlaylists.filter((item) => item.isSelected).map((item) => item.id),
            extraPlaylists: updatedAdditionalPlaylists.filter((item) => item.isSelected).map((item) => item.id),
            playlistsToCreate: newPlaylists.filter((item) => item.isSelected).map((item) => item.name),
            bulk: false,
            channelId: categoryId,
        } as {
            entryIds: string[];
            selectedPlaylist: string[];
            extraPlaylists: string[];
            playlistsToCreate: string[];
            bulk: boolean;
            channelId?: string;
        };

        try {
            const { success, messages, errors } = await postKmsData(addToPlaylistUrl, params);
            if (success) {
                onItemStateChange?.("originalPlaylists", updatedOriginalPlaylists);
                onItemStateChange?.("additionalPlaylists", updatedAdditionalPlaylists);
                messages.forEach((message: string) => showToast({ severity: "success", message }));

                // Invalidate the Suspense fetcher so the next open
                // will refetch fresh playlists data from the server.
                invalidate();
            }
            else {
                errors.forEach((error: string) => showToast({ severity: "error", message: error }));
            }
        }
        catch (e: unknown) {
            showToast({ severity: "error", message: translate("Error adding media to playlist") });
        }

        onCloseMenu?.();
    };

    const resultsCount = playlistsToShow?.length ?? 0;
    const resultsAnnouncement =
        searchTerm && resultsCount === 0
            ? translate("No playlists found")
            : searchTerm
              ? translatePlural("%1 playlist found", "%1 playlists found", [resultsCount])
              : "";

    return (
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <BaseInput
                id={searchInputId}
                placeholder={translate("Search playlist")}
                aria-label={translate("Search playlist")}
                startAdornment={<StyledSearch24Icon />}
                fullWidth={true}
                onChange={handleSearch}
                onKeyDown={onInputKeyDown}
                aria-controls={resultsListId}
            />
            {/* Live region for screen reader announcements */}
            <SpanSrOnly aria-live="polite" aria-atomic="true">
                {resultsAnnouncement}
            </SpanSrOnly>

            <PlaylistsList playlists={playlistsToShow} onChange={handleCheckboxChange} listId={resultsListId} />

            {allowCreate && <CreatePlaylist onNew={handleNewPlaylist} />}
        </form>
    );
};
